import type { PvpEndedFrame, PvpSeat } from '@kenocyber/shared';
import type { PvpClientFailure, PvpClientState } from './connection';

/**
 * What the duel's interface is doing, as one value that cannot contradict
 * itself.
 *
 * ## Why a union and not five booleans
 *
 * `creating`, `waiting`, `connecting`, `reconnecting` and `completed` as
 * separate flags admit states nobody designed: creating AND completed, waiting
 * with a result, running with no match. Each of those renders as something -
 * usually as the wrong thing, briefly, on a real player's screen - and none of
 * them is reachable here, because a state carries only the data that is true
 * while it holds.
 *
 * So `closed` has no match and no socket to leak, `waiting_for_opponent` has a
 * code to show, `running` has a seat and an authoritative world, `completed`
 * has an ending the SERVER named, and `recovering_hunt` has a retry rather than
 * a stale snapshot of a hunt nobody has re-authorised.
 *
 * ## When the hunt gets suspended, and when it does not
 *
 * Opening the lobby, minting an invite and waiting for somebody to take it all
 * leave the hunt running: a player waiting on a code has not left the game, and
 * pausing them for a duel that may never be accepted would cost them the thing
 * they were actually doing. The dedicated transition begins only when the
 * SERVER says the room is `ready` - both seats held - which is `entering_pvp`.
 *
 * ## Nothing here decides a result
 *
 * `completed` is only ever reached by an `ended` frame. A ship at zero health,
 * a clock at zero and a closed socket are not results, and this machine has no
 * transition that could turn one into a winner.
 */

/* -------------------------------------------------------------------------- */
/*  What a match is, once one exists                                          */
/* -------------------------------------------------------------------------- */

export interface PvpMatchIdentity {
  readonly matchId: string;
  /** Present only for a match this browser created. The other side has none. */
  readonly code: string | null;
  readonly seat: PvpSeat;
}

/** A refusal a player can be shown, and what they can do about it. */
export interface PvpUiError {
  /** A stable machine reason. Never a stack, an id, a ticket or a cookie. */
  readonly reason: string;
  /**
   * Whether trying again could work.
   *
   * `pvp_disabled`, `unauthorised`, `rate_limited` and `malformed` are the
   * server saying no on purpose; repeating them repeats the refusal. A network
   * blip is not.
   */
  readonly recoverable: boolean;
}

/**
 * How far a voluntary abandonment has got (ADR 0043).
 *
 * `sending` is the only busy state the duel has, and it exists because the
 * confirmation is the SERVER's `ended` frame and nothing else. Between the click
 * and that frame the player must stay in the match: no arena closed, no lease
 * released, no result invented. `failed` means the intent could not even be
 * written to the socket, which is a refusal to show rather than an exit to hide.
 */
export type PvpAbandonPhase = 'idle' | 'sending' | 'failed';

/* -------------------------------------------------------------------------- */
/*  The states                                                                */
/* -------------------------------------------------------------------------- */

export type PvpUiState =
  /** The panel is not open. Nothing is held: no match, no socket, no lease. */
  | { readonly kind: 'closed' }
  /**
   * A freshly loaded tab is asking the server whether it is already in a duel.
   *
   * ## Why this is a state and not a boolean somewhere (gate V24D.2)
   *
   * Nothing is on screen here: no panel, no lobby, no arena. `resume()` is
   * explicit that opening the panel first is wrong - every page load, for every
   * account, would put the duel panel over the hunt and take it away again.
   *
   * But something IS true that nothing could previously express: this tab may be
   * about to restore an arena. The hunt's bootstrap runs in parallel and can
   * answer first with a run whose ship died earlier, and `NAVE DESTRUÍDA` then
   * lands over an arena that appears a round trip later. Gate V24D.1C
   * photographed exactly that. `panelOpen` could not describe it, because the
   * panel is legitimately shut.
   *
   * So the discovery has a state of its own. It shows nothing and holds nothing
   * - no lease, no socket, no match - and it exists so the shell can give an
   * arena that is one answer away priority over an overlay about the hunt.
   */
  | { readonly kind: 'restoring' }
  /**
   * The lobby, before a choice. The hunt is still running behind it.
   *
   * `lastError` is what a refused CODE leaves behind. A refusal used to move
   * the machine to `error`, whose screen replaces this form - so mistyping six
   * characters cost the player the whole lobby and the only way to try again
   * was to close the panel and reopen it. A code the server did not recognise
   * is not a broken duel; it is a typo, and it belongs where the player can fix
   * it.
   *
   * It is cleared the moment the player edits the code, because a message next
   * to a code it was not about is a message that lies.
   */
  | { readonly kind: 'choosing'; readonly lastError: PvpUiError | null }
  /** A challenge is in flight. */
  | { readonly kind: 'creating'; readonly operation: number }
  /** A code exists and nobody has taken it. The hunt is STILL running. */
  | {
      readonly kind: 'waiting_for_opponent';
      readonly match: PvpMatchIdentity;
      readonly code: string;
      readonly connection: PvpClientState | null;
    }
  /** An accept is in flight. */
  | { readonly kind: 'joining'; readonly operation: number }
  /** Seated, socket opening. Still no reason to disturb the hunt. */
  | {
      readonly kind: 'connecting';
      readonly match: PvpMatchIdentity;
      readonly operation: number;
      /**
       * Whether this socket is REJOINING a duel that was already running.
       *
       * The player sees a different sentence - "reconnecting" rather than
       * "entering the arena" - because those are different situations and only
       * one of them is their fault to wait for. Nothing else branches on it.
       */
      readonly resumed: boolean;
    }
  /**
   * The room is complete and the dedicated transition is running.
   *
   * The one state where the hunt is being taken down and the duel is not yet
   * up. It carries how far the transition got, because a rollback has to undo
   * exactly what was done and nothing else.
   */
  | {
      readonly kind: 'entering_pvp';
      readonly match: PvpMatchIdentity;
      readonly generation: number;
    }
  /** Both seats held, this client is in the arena, and may declare itself. */
  | {
      readonly kind: 'ready';
      readonly match: PvpMatchIdentity;
      readonly generation: number;
      readonly connection: PvpClientState;
      /** Whether a `ready` has already gone out for THIS generation. */
      readonly declared: boolean;
    }
  /** The room says it is starting. Derived from the server, never from a timer. */
  | {
      readonly kind: 'countdown';
      readonly match: PvpMatchIdentity;
      readonly generation: number;
      readonly connection: PvpClientState;
    }
  /** Fighting. The only state in which controls are enabled. */
  | {
      readonly kind: 'running';
      readonly match: PvpMatchIdentity;
      readonly generation: number;
      readonly connection: PvpClientState;
      /**
       * Whether this player has asked to abandon, and how that is going.
       *
       * A sub-state of `running` rather than a state of its own, because the
       * MATCH is still running: the room is ticking, the ship is still being
       * simulated and shot at, and the only thing that changed is that an intent
       * is in flight. Making it a separate kind would have meant a state in which
       * `controlsEnabled` is false while the server is still moving the ship,
       * which is a lie the player pays for.
       *
       * `failed` is the honest answer to a socket that could not be written to:
       * the intent never left, so nothing was abandoned, and the player is still
       * in the fight with a button that now says try again.
       */
      readonly abandon: PvpAbandonPhase;
    }
  /** The socket dropped and the room is holding the seat. Controls are off. */
  | {
      readonly kind: 'reconnecting';
      readonly match: PvpMatchIdentity;
      readonly generation: number;
      readonly connection: PvpClientState;
    }
  /** The server named an outcome. This is the only way to hold a result. */
  | {
      readonly kind: 'completed';
      readonly match: PvpMatchIdentity;
      readonly result: PvpEndedFrame;
    }
  /** Tearing the duel down. The hunt is still hidden and still paused. */
  | { readonly kind: 'leaving'; readonly match: PvpMatchIdentity | null }
  /**
   * The duel is gone and the hunt has not been re-authorised yet.
   *
   * Deliberately its own state rather than a flag on `leaving`: the hunt stays
   * hidden and paused throughout, and a failed bootstrap must be able to sit
   * here offering a retry instead of showing a hunt nobody has re-authorised.
   */
  | {
      readonly kind: 'recovering_hunt';
      readonly operation: number;
      /** Set when a bootstrap has already failed. Never a stale snapshot. */
      readonly failure: PvpUiError | null;
    }
  | { readonly kind: 'error'; readonly error: PvpUiError };

export type PvpUiStateKind = PvpUiState['kind'];

/* -------------------------------------------------------------------------- */
/*  The events                                                                */
/* -------------------------------------------------------------------------- */

export type PvpUiEvent =
  | { readonly type: 'OPEN' }
  /** A reloaded tab began asking `GET /api/pvp/active`. Shows nothing. */
  | { readonly type: 'RESUME_STARTED' }
  /** The discovery answered, and there was nothing to restore. */
  | { readonly type: 'RESUME_ABSENT' }
  | { readonly type: 'CREATE_REQUESTED'; readonly operation: number }
  | {
      readonly type: 'CREATE_SUCCEEDED';
      readonly operation: number;
      readonly match: PvpMatchIdentity;
      readonly code: string;
    }
  | { readonly type: 'CREATE_FAILED'; readonly operation: number; readonly error: PvpUiError }
  | { readonly type: 'JOIN_REQUESTED'; readonly operation: number }
  | {
      readonly type: 'JOIN_SUCCEEDED';
      readonly operation: number;
      readonly match: PvpMatchIdentity;
    }
  | { readonly type: 'JOIN_FAILED'; readonly operation: number; readonly error: PvpUiError }
  /**
   * The server says this account is already in a duel, and named it.
   *
   * Sent after a reload, from an authoritative read rather than from anything
   * the tab remembered - see `PVP_ACTIVE_ROUTE`. It goes straight to
   * `connecting`, because there is nothing to choose: the account has a seat
   * and the only question is whether the socket comes back before the room's
   * grace expires.
   */
  | {
      readonly type: 'RESUME_FOUND';
      readonly operation: number;
      readonly match: PvpMatchIdentity;
    }
  /** The authoritative client state moved. The single source of match truth. */
  | {
      readonly type: 'AUTHORITATIVE_STATE';
      readonly generation: number;
      readonly state: PvpClientState;
    }
  /**
   * The room is complete and the dedicated transition is starting.
   *
   * Dispatched by the controller when the SERVER reports the match at `ready` -
   * both seats held. It is a separate event rather than a consequence of an
   * authoritative frame because the transition it starts is a sequence with a
   * rollback, and a reducer must not be the thing that decides to run one.
   */
  | {
      readonly type: 'ENTER_STARTED';
      readonly generation: number;
      readonly match: PvpMatchIdentity;
    }
  /** The dedicated transition finished; the arena is up. */
  | { readonly type: 'ENTERED'; readonly generation: number; readonly state: PvpClientState }
  /** The transition failed and was rolled back. */
  | { readonly type: 'ENTER_FAILED'; readonly generation: number; readonly error: PvpUiError }
  | { readonly type: 'READY_REQUESTED'; readonly generation: number }
  | { readonly type: 'ENDED'; readonly generation: number; readonly result: PvpEndedFrame }
  | {
      readonly type: 'CONNECTION_FAILED';
      readonly generation: number;
      readonly failure: PvpClientFailure;
      readonly detail: string | null;
    }
  /**
   * The player pressed ABANDONAR PARTIDA and the intent is going out.
   *
   * Dispatched BEFORE the socket is written to, so a second click in the same
   * frame finds the state already busy. It is not a result and it is not an
   * exit: the only thing that ends a match is an `ENDED` from the room.
   */
  | { readonly type: 'ABANDON_REQUESTED'; readonly generation: number }
  /** The intent could not be sent. The player is still in the match. */
  | { readonly type: 'ABANDON_FAILED'; readonly generation: number }
  | { readonly type: 'LEAVE_REQUESTED' }
  | { readonly type: 'HUNT_RECOVERY_STARTED'; readonly operation: number }
  | { readonly type: 'HUNT_RECOVERED'; readonly operation: number }
  | {
      readonly type: 'HUNT_RECOVERY_FAILED';
      readonly operation: number;
      readonly error: PvpUiError;
    }
  | { readonly type: 'RESET_ERROR' }
  /**
   * The player started typing a different code.
   *
   * Dispatched by the panel on the first edit after a refusal, so the message
   * disappears with the code it was about instead of hovering over the next
   * one.
   */
  | { readonly type: 'CODE_EDITED' }
  | { readonly type: 'CLOSED' };

/* -------------------------------------------------------------------------- */
/*  Guards                                                                    */
/* -------------------------------------------------------------------------- */

/** Whether the duel currently holds the hunt paused and hidden. */
export function holdsHunt(state: PvpUiState): boolean {
  switch (state.kind) {
    case 'entering_pvp':
    case 'ready':
    case 'countdown':
    case 'running':
    case 'reconnecting':
    case 'completed':
    case 'leaving':
    case 'recovering_hunt':
      return true;
    default:
      /*
       * The lobby, a minted invite and a socket that has not reached a full
       * room all leave the hunt alone. A player waiting on a code has not left
       * the game, and pausing them for a duel nobody has accepted would cost
       * them the thing they were actually doing.
       */
      return false;
  }
}

/** Whether the arena is open, so a close must be published on the way out. */
export function arenaIsOpen(state: PvpUiState): boolean {
  switch (state.kind) {
    case 'ready':
    case 'countdown':
    case 'running':
    case 'reconnecting':
    case 'completed':
      return true;
    default:
      // `entering_pvp` deliberately answers false: the transition publishes
      // `pvp:open` at its last step, and a rollback before that must not close
      // a scene that was never opened.
      return false;
  }
}

/** Whether a player may drive their ship. Only ever while the room is running. */
export function controlsEnabled(state: PvpUiState): boolean {
  return state.kind === 'running';
}

/** The generation a state belongs to, or null where the concept does not apply. */
/**
 * The duel a state is about, if it is about one.
 *
 * Every state from `connecting` onwards carries the identity; the ones before
 * it have nothing to carry. Used where something has to be recorded about the
 * match being left, before the state that names it is thrown away.
 */
export function matchOf(state: PvpUiState): PvpMatchIdentity | null {
  switch (state.kind) {
    case 'waiting_for_opponent':
    case 'connecting':
    case 'entering_pvp':
    case 'ready':
    case 'countdown':
    case 'running':
    case 'reconnecting':
    case 'completed':
    case 'leaving':
      return state.match;
    default:
      return null;
  }
}

export function generationOf(state: PvpUiState): number | null {
  switch (state.kind) {
    case 'entering_pvp':
    case 'ready':
    case 'countdown':
    case 'running':
    case 'reconnecting':
      return state.generation;
    default:
      return null;
  }
}

/**
 * Whether an answer from an asynchronous operation is still wanted.
 *
 * The rule that makes a double click harmless and a slow network honest: a
 * response may only change the state if it belongs to the operation the machine
 * is still waiting for. Two creates in one frame produce two requests, and the
 * first answer to arrive is dropped unless it is the one that was asked for
 * last.
 */
function operationMatches(state: PvpUiState, operation: number): boolean {
  switch (state.kind) {
    case 'creating':
    case 'joining':
    case 'connecting':
    case 'recovering_hunt':
      return state.operation === operation;
    default:
      return false;
  }
}

/** Whether a socket-borne event belongs to the connection the machine holds. */
function generationMatches(state: PvpUiState, generation: number): boolean {
  const current = generationOf(state);
  return current !== null && current === generation;
}

/* -------------------------------------------------------------------------- */
/*  The reducer                                                               */
/* -------------------------------------------------------------------------- */

export const PVP_INITIAL_STATE: PvpUiState = { kind: 'closed' };

/**
 * The whole machine.
 *
 * Every branch that returns `state` unchanged is a transition somebody could
 * have written and should not have: a welcome from a socket that has been
 * replaced, a create answering after the player left, an ending for a match
 * that is not the one on screen. Dropping them here - once - is what keeps the
 * controller from having to remember to check.
 */
export function pvpReducer(state: PvpUiState, event: PvpUiEvent): PvpUiState {
  switch (event.type) {
    case 'OPEN':
      /*
       * From closed, and from a discovery that has not answered yet.
       *
       * Opening a panel that is already open must not throw away a match in
       * progress - so every other state is left alone. `restoring` is included
       * because it holds nothing and shows nothing: a player who presses PVP
       * while the tab is still asking has told us what they want, and
       * `RESUME_FOUND` already refuses to drag them back out of it.
       */
      return state.kind === 'closed' || state.kind === 'restoring'
        ? { kind: 'choosing', lastError: null }
        : state;

    case 'RESUME_STARTED':
      // Only from closed: a tab that is showing anything at all is not a tab
      // that just loaded, and must not be interrupted by a discovery.
      return state.kind === 'closed' ? { kind: 'restoring' } : state;

    case 'RESUME_ABSENT':
      /*
       * Back to closed, and only from the discovery itself.
       *
       * A late answer must never close a panel the player opened while it was
       * in flight, which is why this checks the state rather than trusting the
       * caller.
       */
      return state.kind === 'restoring' ? { kind: 'closed' } : state;

    case 'CREATE_REQUESTED':
      return state.kind === 'choosing' ? { kind: 'creating', operation: event.operation } : state;

    case 'CREATE_SUCCEEDED':
      if (!operationMatches(state, event.operation) || state.kind !== 'creating') {
        return state;
      }
      return {
        kind: 'waiting_for_opponent',
        match: event.match,
        code: event.code,
        connection: null,
      };

    case 'CREATE_FAILED':
      if (!operationMatches(state, event.operation) || state.kind !== 'creating') {
        return state;
      }
      return { kind: 'error', error: event.error };

    case 'JOIN_REQUESTED':
      return state.kind === 'choosing' ? { kind: 'joining', operation: event.operation } : state;

    case 'RESUME_FOUND':
      /*
       * Only from `choosing`, and only for the current operation.
       *
       * A player who reached for something else while the answer was in flight
       * has already told us what they want, and a duel they are being put back
       * into is not more important than that.
       */
      /*
       * Only from , and the operation is carried INTO the new state
       * rather than checked against the old one - exactly as CREATE_REQUESTED
       * does.  answers false for every state that has no
       * operation of its own, so asking it here would refuse every resume.
       *
       * A player who reached for something else while the answer was in flight
       * has already left , and is not dragged back.
       */
      if (state.kind !== 'choosing') {
        return state;
      }
      return { kind: 'connecting', match: event.match, operation: event.operation, resumed: true };

    case 'JOIN_SUCCEEDED':
      if (!operationMatches(state, event.operation) || state.kind !== 'joining') {
        return state;
      }
      return {
        kind: 'connecting',
        match: event.match,
        operation: event.operation,
        resumed: false,
      };

    case 'JOIN_FAILED':
      if (!operationMatches(state, event.operation) || state.kind !== 'joining') {
        // An answer to an operation nobody is waiting for any more. A refusal
        // from the code the player has already replaced must not appear beside
        // the one they are typing now.
        return state;
      }
      /*
       * Back to the LOBBY, carrying the refusal - not to the error screen.
       *
       * Nothing was taken by a join that failed: no lease, no socket, no arena,
       * no bootstrap. So there is nothing to unwind and nowhere to go except
       * the form the player was already looking at.
       */
      return { kind: 'choosing', lastError: event.error };

    case 'AUTHORITATIVE_STATE':
      return applyAuthoritative(state, event.generation, event.state);

    case 'ENTER_STARTED':
      if (state.kind !== 'connecting' && state.kind !== 'waiting_for_opponent') {
        // Only from a seat that is waiting for the room to fill. Anything else
        // is a duplicate, or a room that is already up.
        return state;
      }
      return { kind: 'entering_pvp', match: event.match, generation: event.generation };

    case 'ENTERED':
      if (state.kind !== 'entering_pvp' || state.generation !== event.generation) {
        return state;
      }
      return {
        kind: 'ready',
        match: state.match,
        generation: state.generation,
        connection: event.state,
        declared: false,
      };

    case 'ENTER_FAILED':
      if (state.kind !== 'entering_pvp' || state.generation !== event.generation) {
        return state;
      }
      return { kind: 'error', error: event.error };

    case 'READY_REQUESTED':
      if (state.kind !== 'ready' || state.generation !== event.generation || state.declared) {
        // One `ready` per generation. A second click is not a second player.
        return state;
      }
      return { ...state, declared: true };

    case 'ENDED': {
      if (!generationMatches(state, event.generation)) {
        return state;
      }
      const match = 'match' in state ? state.match : null;
      if (!match) {
        return state;
      }
      // The ONLY road to a result, and it comes from the server naming one.
      return { kind: 'completed', match, result: event.result };
    }

    case 'CONNECTION_FAILED':
      if (!generationMatches(state, event.generation)) {
        return state;
      }
      return {
        kind: 'error',
        error: { reason: event.detail ?? event.failure, recoverable: isRecoverable(event.failure) },
      };

    case 'ABANDON_REQUESTED':
      /*
       * Only from a fight, only for the connection this machine holds, and only
       * once at a time. A double click is not two abandonments, and a click
       * arriving for a generation that has been replaced belongs to a match this
       * client is no longer in.
       */
      if (
        state.kind !== 'running' ||
        state.generation !== event.generation ||
        state.abandon === 'sending'
      ) {
        return state;
      }
      return { ...state, abandon: 'sending' };

    case 'ABANDON_FAILED':
      // Only an intent that was actually in flight can fail to have gone.
      if (
        state.kind !== 'running' ||
        state.generation !== event.generation ||
        state.abandon !== 'sending'
      ) {
        return state;
      }
      return { ...state, abandon: 'failed' };

    case 'LEAVE_REQUESTED':
      if (state.kind === 'closed' || state.kind === 'leaving') {
        return state;
      }
      if (!holdsHunt(state)) {
        /*
         * Nothing was taken from the hunt, so nothing has to be given back:
         * no lease, no visibility message and no bootstrap. Leaving the lobby
         * is closing a panel.
         */
        return { kind: 'closed' };
      }
      return { kind: 'leaving', match: 'match' in state ? state.match : null };

    case 'HUNT_RECOVERY_STARTED':
      /*
       * From `leaving` the first time, and from `recovering_hunt` for a RETRY.
       *
       * The retry has to be here: a bootstrap that failed leaves the machine
       * sitting in `recovering_hunt` holding the hunt hidden, and the only way
       * out is asking again - under a NEW operation id, so the answer to the
       * attempt that already failed cannot resolve this one.
       */
      if (state.kind !== 'leaving' && state.kind !== 'recovering_hunt') {
        return state;
      }
      return { kind: 'recovering_hunt', operation: event.operation, failure: null };

    case 'HUNT_RECOVERED':
      if (!operationMatches(state, event.operation) || state.kind !== 'recovering_hunt') {
        return state;
      }
      return { kind: 'closed' };

    case 'HUNT_RECOVERY_FAILED':
      if (!operationMatches(state, event.operation) || state.kind !== 'recovering_hunt') {
        return state;
      }
      // Stays here, holding the hunt hidden and paused, offering a retry. The
      // one thing it must never do is show a hunt nobody re-authorised.
      return { ...state, failure: event.error };

    case 'CODE_EDITED':
      return state.kind === 'choosing' && state.lastError !== null
        ? { kind: 'choosing', lastError: null }
        : state;

    case 'RESET_ERROR':
      return state.kind === 'error' ? { kind: 'choosing', lastError: null } : state;

    case 'CLOSED':
      return { kind: 'closed' };
  }
}

/**
 * Folds an authoritative client state into the interface.
 *
 * The match's own lifecycle - `ready`, `running`, `completed` - is the server's
 * and arrives here; what this decides is only which SCREEN corresponds to it,
 * and it refuses anything from a generation it is not holding.
 */
function applyAuthoritative(
  state: PvpUiState,
  generation: number,
  next: PvpClientState,
): PvpUiState {
  if (state.kind === 'waiting_for_opponent') {
    // Still waiting: the hunt is untouched and the panel just shows progress.
    // The controller starts the dedicated transition when the room is complete.
    return { ...state, connection: next };
  }
  if (state.kind === 'connecting') {
    return state;
  }
  if (!generationMatches(state, generation)) {
    /*
     * A socket that has been replaced, still talking. The room applies the
     * same rule to a superseded seat; applying it here too is what stops an
     * old generation repainting a live duel.
     */
    return state;
  }
  switch (state.kind) {
    case 'ready':
    case 'countdown':
    case 'running':
    case 'reconnecting': {
      const base = { match: state.match, generation: state.generation, connection: next };
      if (next.phase === 'connecting' || next.phase === 'authorising') {
        return { kind: 'reconnecting', ...base };
      }
      if (next.matchState === 'running') {
        /*
         * An abandonment in flight SURVIVES an authoritative frame.
         *
         * The room sends one of these twenty times a second, and rebuilding
         * `running` from the frame alone would clear the busy flag within 50 ms -
         * the button would go back to ABANDONAR PARTIDA while the intent was
         * still travelling, and the next click would send a second one.
         *
         * It does NOT survive a reconnection: coming back through `reconnecting`
         * means the socket went, and an intent that may never have arrived must
         * not leave the player looking at a button that says it did.
         */
        return {
          kind: 'running',
          ...base,
          abandon: state.kind === 'running' ? state.abandon : 'idle',
        };
      }
      if (state.kind === 'ready') {
        return { ...state, connection: next };
      }
      // Back to ready from a reconnection: the room has not started ticking.
      return { kind: 'ready', ...base, declared: false };
    }
    default:
      return state;
  }
}

/**
 * Whether a connection failure is worth offering to retry.
 *
 * The definitive ones are the server saying no on purpose. `superseded` is in
 * that list and is not an error at all in spirit: another tab took the seat,
 * and fighting for it back would produce two tabs taking it from each other.
 */
function isRecoverable(failure: PvpClientFailure): boolean {
  switch (failure) {
    case 'pvp_disabled':
    case 'unauthorised':
    case 'rate_limited':
    case 'malformed':
    case 'superseded':
    case 'rules_version_mismatch':
      return false;
    default:
      return true;
  }
}
